import { useMemo } from 'react'

import { PublicClient, createPublicClient, http } from 'viem'
import {
    apeChain,
    arbitrum,
    avalanche,
    base,
    blast,
    bsc,
    celo,
    mainnet,
    megaethTestnet,
    mode,
    monadTestnet,
    opBNB,
    optimism,
    polygon,
    scroll,
    sepolia,
} from 'viem/chains'

import { ChainId } from '@src/configs/chainInfo'
import { wagmiUnichain } from '@src/configs/customChains'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

// Import unichain from customChains.ts instead of defining it here

// Define a mapping of Chain IDs to Viem chains for testnet chains
const testnetViemChainConfigs: Record<number, any> = {
    [ChainId.Sepolia]: sepolia,
    [ChainId.MonadTestnet]: monadTestnet,
    [ChainId.MegaEthTestnet]: megaethTestnet,
}

// Define a mapping of chain IDs to Viem chain configurations
export const viemChainConfigs: Record<number, any> = isFeatureEnabled('USE_TESTNETS_ONLY')
    ? testnetViemChainConfigs
    : {
          [ChainId.Ethereum]: mainnet,
          [ChainId.Optimism]: optimism,
          [ChainId.Polygon]: polygon,
          [ChainId.Arbitrum]: arbitrum,
          [ChainId.Avalanche]: avalanche,
          [ChainId.Celo]: celo,
          [ChainId.Base]: base,
          [ChainId.Blast]: blast,
          [ChainId.Bsc]: bsc,
          [ChainId.Opbnb]: opBNB,
          [ChainId.Scroll]: scroll,
          [ChainId.Mode]: mode,
          [ChainId.Unichain]: wagmiUnichain,
          [ChainId.ApeChain]: apeChain,
      }

// Global cache to store clients by chainId - persists throughout app lifecycle
// Using any to avoid type conflicts between different viem versions
const clientCache: Record<number, any> = {}
// Create a logger instance for this module
const logger = createContextLogger('useViemClient')

export const getViemClient = (chainId: number) => {
    const chainConfig = viemChainConfigs[chainId]

    if (!chainConfig) {
        logger.error(`Unsupported chainId: ${chainId}`)
        return void 0
    }

    // Check if client already exists in cache
    if (!clientCache[chainId]) {
        // Create and cache new client
        clientCache[chainId] = createPublicClient({
            chain: chainConfig,
            transport: http(),
        })
        logger.debug(`Created new client for chainId: ${chainId}`)
    }

    return clientCache[chainId]
}

/**
 * Custom hook to get the Viem public client based on a chain ID.
 * Uses a global cache to ensure only one client is created per chainId during the app lifecycle.
 * @param chainId - The chain ID for which to get the client.
 * @returns The Viem public client for the specified chain.
 */
export const useViemClient = (chainId: number) => {
    // Using useMemo so the hook maintains referential equality between renders
    // The actual caching is done in getViemClient
    const publicClient = useMemo(() => {
        return getViemClient(chainId)
    }, [chainId])

    return publicClient
}
